// The client's task list: what the care plan asked for, and what the office
// actually delivers. The plan owns which tasks exist — adding or removing care
// is a plan change and needs the nurse's signature. The office owns when and
// how: which days, how often, and the wording the caregiver reads.
import { supabase } from './supabase';

export const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
export const FREQUENCIES = [
  ['every_visit', 'Every visit'],
  ['daily',       'Once a day'],
  ['weekly',      'Chosen days'],
  ['as_needed',   'As needed'],
];
export const FREQ_LABEL = Object.fromEntries(FREQUENCIES);

// How the catalogue classifies work. The order is the order a nurse reads it in.
export const KINDS = [
  ['adl',         'Activities of daily living'],
  ['iadl',        'Household and errands'],
  ['treatment',   'Treatments and procedures'],
  ['observation', 'Observation and monitoring'],
  ['skilled',     'Teaching'],
  ['log',         'Records to complete'],
];
export const KIND_LABEL = Object.fromEntries(KINDS);

export const getClientTasks = (clientId) =>
  supabase.from('client_tasks')
    .select(`id, task_id, required, days_of_week, frequency, instructions, active,
             plan_days_of_week, plan_instructions, plan_frequency,
             office_edited, office_edited_at, office_note,
             task:task_catalog ( id, code, name, category, kind, capture, instructions, skill_kind )`)
    .eq('client_id', clientId)
    .is('service_id', null)
    .order('active', { ascending: false });

export const setTaskSchedule = async (id, { days, frequency, instructions, note }) => {
  const { data, error } = await supabase.rpc('client_task_set_schedule', {
    p_id: id,
    p_days: frequency === 'weekly' ? (days || []) : null,
    p_frequency: frequency,
    p_instructions: instructions || null,
    p_note: note || null,
  });
  if (error) throw new Error(error.message);
  return data;
};

export const resetTaskToPlan = async (id) => {
  const { data, error } = await supabase.rpc('client_task_reset_to_plan', { p_id: id });
  if (error) throw new Error(error.message);
  return data;
};

// What a caregiver will actually see on a given weekday.
export const runsOn = (t, dow) =>
  t.frequency !== 'weekly' || !t.days_of_week?.length || t.days_of_week.includes(dow);

export const scheduleLabel = (t) => {
  if (t.frequency !== 'weekly') return FREQ_LABEL[t.frequency] || t.frequency;
  const d = t.days_of_week || [];
  if (!d.length) return 'No days chosen';
  if (d.length === 7) return 'Every day';
  return d.slice().sort((a, b) => a - b).map(i => DAYS[i]).join(' ');
};

// True when what we deliver no longer matches what the nurse signed.
export const differsFromPlan = (t) => {
  if (!t.office_edited) return false;
  const same = (a, b) => JSON.stringify([...(a || [])].sort()) === JSON.stringify([...(b || [])].sort());
  return t.frequency !== t.plan_frequency
      || !same(t.days_of_week, t.plan_days_of_week)
      || (t.instructions || '') !== (t.plan_instructions || '');
};

export const planScheduleLabel = (t) =>
  scheduleLabel({ frequency: t.plan_frequency, days_of_week: t.plan_days_of_week });

export const groupByKind = (tasks) => {
  const g = {};
  tasks.forEach(t => { const k = t.task?.kind || 'adl'; (g[k] = g[k] || []).push(t); });
  return KINDS.filter(([k]) => g[k]?.length).map(([k, label]) => [k, label, g[k]]);
};
